import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { Student, UserRole, Branch, ClassRoom, ProgramPrice } from '../types/db';
import {
  Search,
  Trash2,
  Loader2,
  UserPlus,
  Phone,
  CheckCircle,
  X,
  Shield,
  User,
  DollarSign,
  Calendar,
  Key,
  School,
  Printer,
  History,
  ChevronDown,
  Banknote,
  Save,
  Plus,
  RefreshCw,
  Layers,
  UserX,
  UserCheck,
} from 'lucide-react';
import { supabase } from '../lib/supabaseClient';
import { useAuth } from '../context/AuthContext';
import { getAccountingPeriod } from '../lib/accountingPeriod';

const FORM_STORAGE_KEY = 'delta_registration_draft';

export const StudentList: React.FC<{
  students: Student[];
  branches: Branch[];
  activeBranchId: string;
  onAddStudent: () => void;
  onDeleteStudent: (id: string) => void;
}> = ({ students, branches, activeBranchId, onAddStudent, onDeleteStudent }) => {
  const { profile: currentUser } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'ALL' | 'ACTIVE' | 'INACTIVE'>('ACTIVE');
  const [isStatusChanging, setIsStatusChanging] = useState<string | null>(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [viewingFinance, setViewingFinance] = useState<Student | null>(null);
  const [isAddPaymentOpen, setIsAddPaymentOpen] = useState(false);
  const [loadingPayment, setLoadingPayment] = useState(false);
  const [isVoiding, setIsVoiding] = useState<string | null>(null);

  const handleToggleStatus = async (student: Student) => {
    const isActive = student.is_active !== false;
    const actionText = isActive ? 'menonaktifkan' : 'mengaktifkan kembali';
    const confirmMessage = isActive
      ? `Apakah Anda yakin ingin menonaktifkan siswa "${student.name}"?\n\nSiswa ini akan dipindahkan ke daftar nonaktif dan tidak akan muncul di daftar absensi kelas.`
      : `Apakah Anda yakin ingin mengaktifkan kembali siswa "${student.name}"?\n\nSiswa ini akan kembali menjadi siswa aktif.`;

    if (!window.confirm(confirmMessage)) return;

    setIsStatusChanging(student.id);
    try {
      const { error } = await supabase
        .from('students')
        .update({
          is_active: !isActive,
          status: !isActive ? 'ACTIVE' : 'NONAKTIF',
          notes:
            `${student.notes || ''}\n[SYSTEM: Status diubah menjadi ${!isActive ? 'AKTIF' : 'NONAKTIF'} pada ${new Date().toLocaleDateString('id-ID')}]`.trim(),
        })
        .eq('id', student.id);

      if (error) throw error;

      window.alert(`Siswa "${student.name}" berhasil ${actionText}!`);
      onAddStudent();
    } catch (e: any) {
      window.alert('Gagal mengubah status: ' + e.message);
    } finally {
      setIsStatusChanging(null);
    }
  };

  const [isPromoteOpen, setIsPromoteOpen] = useState(false);
  const [promotingStudent, setPromotingStudent] = useState<Student | null>(null);
  const [promoteClasses, setPromoteClasses] = useState<ClassRoom[]>([]);
  const [isLoadingPromoteClasses, setIsLoadingPromoteClasses] = useState(false);
  const [promoteProgramPrices, setPromoteProgramPrices] = useState<ProgramPrice[]>([]);
  const [isSubmittingPromote, setIsSubmittingPromote] = useState(false);
  const [promoteFormData, setPromoteFormData] = useState({
    class_id: '',
    program: 'REGULER',
    customProgram: '',
    totalCourseFee: 0,
    registrationFee: 0,
    discount: 0,
    paymentType: 'CASH' as 'CASH' | 'ANGSUR',
    installments: 2,
    initialPayment: 0,
    paymentDate: new Date().toISOString().split('T')[0],
    joinDate: new Date().toISOString().split('T')[0],
  });

  const [availableClasses, setAvailableClasses] = useState<ClassRoom[]>([]);
  const [isLoadingClasses, setIsLoadingClasses] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    class_id: '',
    phone: '',
    parent_name: '',
    parent_phone: '',
    address: '',
    school_origin: '',
    program: 'REGULER',
    customProgram: '',
    discount: 0,
    registrationFee: 50000,
    totalCourseFee: 0,
    paymentType: 'CASH' as 'CASH' | 'ANGSUR',
    installments: 2,
    password: '',
    joinDate: new Date().toISOString().split('T')[0],
    paymentDate: new Date().toISOString().split('T')[0],
    initialPayment: 0,
  });

  const [paymentForm, setPaymentForm] = useState({
    amount: 0,
    date: new Date().toISOString().split('T')[0],
    notes: '',
    installmentIndex: -1,
  });

  const fetchClasses = useCallback(async (targetBranch: string) => {
    if (!targetBranch || targetBranch === 'ALL') return;
    setIsLoadingClasses(true);
    try {
      const { data } = await supabase
        .from('classes')
        .select('*')
        .eq('branch_id', targetBranch)
        .order('name');
      if (data) setAvailableClasses(data);
    } catch (e) {
      /* console removed */
    } finally {
      setIsLoadingClasses(false);
    }
  }, []);

  useEffect(() => {
    if (isFormOpen) {
      const resolvedBranchId =
        currentUser?.branch_id && currentUser.branch_id !== 'HEADQUARTERS'
          ? currentUser.branch_id
          : activeBranchId !== 'ALL'
            ? activeBranchId
            : branches[0]?.id || null;
      if (resolvedBranchId) {
        fetchClasses(resolvedBranchId);
        setFormData((prev) => ({ ...prev, class_id: '' }));
      }
    }
  }, [isFormOpen, activeBranchId, currentUser, branches, fetchClasses]);

  useEffect(() => {
    const saved = localStorage.getItem(FORM_STORAGE_KEY);
    if (saved) {
      try {
        setFormData((prev) => ({ ...prev, ...JSON.parse(saved) }));
      } catch (e) {
        /* console removed */
      }
    }
  }, []);

  useEffect(() => {
    if (isFormOpen) {
      localStorage.setItem(FORM_STORAGE_KEY, JSON.stringify(formData));
    }
  }, [formData, isFormOpen]);

  const filtered = students.filter((s) => {
    const matchesSearch = (s.name || '').toLowerCase().includes(searchTerm.toLowerCase());
    const isActive = s.is_active !== false;

    if (statusFilter === 'ACTIVE') {
      return matchesSearch && isActive;
    }
    if (statusFilter === 'INACTIVE') {
      return matchesSearch && !isActive;
    }
    return matchesSearch;
  });

  const feeCalculation = useMemo(() => {
    const discountAmount = (Number(formData.totalCourseFee) * Number(formData.discount)) / 100;
    const finalCourseFee = Number(formData.totalCourseFee) - discountAmount;
    const totalBill = finalCourseFee + Number(formData.registrationFee);
    const remainingDebt = Math.max(0, totalBill - Number(formData.initialPayment));

    return { totalBill, remainingDebt, discountAmount };
  }, [
    formData.totalCourseFee,
    formData.discount,
    formData.registrationFee,
    formData.initialPayment,
  ]);

  const canAddStudent =
    currentUser &&
    [
      UserRole.FINANCE,
      UserRole.MASTER_ADMIN,
      UserRole.SUPER_ADMIN,
      UserRole.CENTRAL_ADMIN,
    ].includes(currentUser.role);

  const canViewFinance =
    currentUser &&
    [
      UserRole.FINANCE,
      UserRole.MASTER_ADMIN,
      UserRole.SUPER_ADMIN,
      UserRole.CENTRAL_ADMIN,
    ].includes(currentUser.role);

  const handlePrintReceipt = (student: Student, payment: any) => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    const renderCard = (typeLabel: string) => `
      <div class="receipt-card">
        <div class="watermark uppercase tracking-widest">Bimbel Delta</div>
        
        <!-- Header -->
        <div class="flex justify-between items-start border-b border-slate-200 pb-3 mb-3 relative z-10">
          <div>
            <div class="flex items-center gap-2">
              <h1 class="text-xl font-black text-indigo-600 tracking-tighter uppercase">Bimbel DELTA</h1>
              <span class="text-[8px] font-black bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded tracking-widest uppercase">${typeLabel}</span>
            </div>
            <p class="text-[8px] font-black text-slate-400 uppercase tracking-[0.25em] mt-0.5">Official Payment Receipt</p>
          </div>
          <div class="text-right">
            <p class="text-[8px] font-black text-slate-400 uppercase tracking-widest">No. Transaksi</p>
            <p class="text-[11px] font-mono font-bold text-slate-800">${payment.id || 'TRX-MANUAL'}</p>
            <p class="text-[8px] font-bold text-indigo-600 mt-0.5 uppercase">${new Date(payment.date).toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' })}</p>
          </div>
        </div>
        
        <!-- Detail Pembayaran -->
        <div class="space-y-2.5 relative z-10 my-auto">
          <div class="grid grid-cols-3 gap-2">
            <div class="col-span-1 text-[9px] font-black text-slate-400 uppercase tracking-widest">Telah terima dari</div>
            <div class="col-span-2 text-xs font-black text-slate-800 uppercase border-b border-slate-100 pb-0.5">: ${student.name}</div>
          </div>
          <div class="grid grid-cols-3 gap-2">
            <div class="col-span-1 text-[9px] font-black text-slate-400 uppercase tracking-widest">Untuk Pembayaran</div>
            <div class="col-span-2 text-xs font-bold text-slate-600 border-b border-slate-100 pb-0.5">: ${payment.notes || 'Angsuran / Pelunasan Kursus'}</div>
          </div>
          
          <div class="bg-indigo-50/80 p-3 rounded-lg flex justify-between items-center my-2.5 border border-indigo-100 shadow-inner">
            <span class="text-[10px] font-black text-indigo-700 tracking-widest uppercase">JUMLAH (IDR):</span>
            <span class="text-xl font-black text-indigo-700">Rp ${Number(payment.amount).toLocaleString('id-ID')}</span>
          </div>
        </div>

        <!-- Footer & Tanda Tangan -->
        <div class="flex justify-between items-end relative z-10 pt-1.5 border-t border-slate-100">
          <div class="text-[7px] text-slate-400 font-bold max-w-xs leading-relaxed uppercase tracking-tighter">
            * Kwitansi sah pembayaran database Bimbel Delta. Mohon disimpan.
          </div>
          <div class="text-center w-36">
            <p class="text-[8px] font-black text-slate-400 uppercase tracking-widest mb-7">Petugas Administrasi,</p>
            <div class="border-t border-slate-800 pt-0.5 text-[9px] font-black uppercase text-slate-800 tracking-tight">
              ${currentUser?.name || 'Authorized Admin'}
            </div>
          </div>
        </div>
      </div>
    `;

    const receiptHtml = `
      <html>
        <head>
          <title>Kwitansi - ${student.name}</title>
          <script src="https://cdn.tailwindcss.com"></script>
          <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;700;900&display=swap" rel="stylesheet">
          <style>
            @media print {
              .no-print { display: none; }
              @page {
                size: A4 portrait;
                margin: 0;
              }
              body {
                margin: 0;
                padding: 10mm;
              }
            }
            body {
              font-family: 'Inter', sans-serif;
              background: #fff;
              color: #0f172a;
              margin: 0;
              padding: 10mm;
              box-sizing: border-box;
            }
            .page-container {
              width: 100%;
              max-width: 190mm;
              margin: 0 auto;
              display: flex;
              flex-direction: column;
              gap: 8mm;
            }
            .receipt-card {
              height: 128mm;
              border: 2px dashed #94a3b8;
              padding: 16px 20px;
              border-radius: 12px;
              position: relative;
              overflow: hidden;
              display: flex;
              flex-direction: column;
              justify-content: space-between;
              box-sizing: border-box;
              background: #ffffff;
            }
            .divider-line {
              border-top: 1px dashed #cbd5e1;
              position: relative;
              margin: 2mm 0;
            }
            .divider-line::after {
              content: '✂ POTONG DI SINI';
              position: absolute;
              top: -8px;
              right: 10px;
              background: #fff;
              padding: 0 8px;
              font-size: 8px;
              font-weight: 900;
              color: #94a3b8;
            }
            .watermark {
              position: absolute;
              top: 50%;
              left: 50%;
              transform: translate(-50%, -50%) rotate(-20deg);
              font-size: 50px;
              font-weight: 900;
              color: rgba(79, 70, 229, 0.04);
              z-index: 0;
              pointer-events: none;
              white-space: nowrap;
            }
          </style>
        </head>
        <body onload="window.print()">
          <div class="page-container">
            ${renderCard('Lembar Siswa')}
            <div class="divider-line"></div>
            ${renderCard('Lembar Admin / Arsip')}
          </div>
        </body>
      </html>
    `;

    printWindow.document.write(receiptHtml);
    printWindow.document.close();
  };

  const handleVoidPayment = async (
    studentId: string,
    paymentId: string,
    originalAmount: number
  ) => {
    const confirmVoid = window.confirm(
      `Apakah Anda yakin ingin membatalkan (Void) pembayaran sebesar Rp ${originalAmount.toLocaleString()}?\n\nTransaksi pembalik akan dicatat otomatis untuk audit laporan keuangan.`
    );

    if (!confirmVoid) return;

    setIsVoiding(paymentId);
    try {
      const { error } = await supabase.functions.invoke('void-cash-payment', {
        body: {
          student_id: studentId,
          payment_id: paymentId,
          void_reason: 'Pembatalan oleh Admin',
        },
      });

      if (error) throw error;

      window.alert('Transaksi berhasil di-void!');
      setViewingFinance(null);
      onAddStudent();
    } catch (err: any) {
      window.alert('Gagal melakukan Void: ' + err.message);
    } finally {
      setIsVoiding(null);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.password || !formData.class_id) {
      return window.alert('Mohon lengkapi identitas dan pilih kelas siswa.');
    }

    setIsSubmitting(true);
    try {
      const finalProgram =
        formData.program === 'INPUT MANUAL' ? formData.customProgram : formData.program;

      const safeInitialPayment =
        formData.paymentType === 'CASH'
          ? feeCalculation.totalBill
          : Number(formData.initialPayment);

      const { error } = await supabase.functions.invoke('create-student', {
        body: {
          email: formData.email,
          password: formData.password,
          name: formData.name,
          class_id: formData.class_id,
          phone_number: formData.phone,
          parent_name: formData.parent_name,
          parent_phone: formData.parent_phone,
          address: formData.address,
          school_origin: formData.school_origin,
          program_choice: finalProgram,
          registration_fee: Number(formData.registrationFee),
          total_course_fee: feeCalculation.totalBill,
          discount_percent: Number(formData.discount),
          payment_type: formData.paymentType,
          installment_count: formData.paymentType === 'ANGSUR' ? formData.installments : 1,
          joined_at: formData.joinDate,
          payment_date: formData.paymentDate,
          initial_payment_amount: safeInitialPayment,
        },
      });

      if (error) throw error;

      window.alert(`BERHASIL!\nSiswa: ${formData.name} telah diaktifkan di system.`);
      localStorage.removeItem(FORM_STORAGE_KEY);
      setIsFormOpen(false);
      onAddStudent();
    } catch (err: any) {
      window.alert('Gagal Pendaftaran: ' + (err.message || 'Unknown error'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleManualPayment = async () => {
    if (!viewingFinance || paymentForm.amount <= 0) return;

    setLoadingPayment(true);
    try {
      const { error } = await supabase.functions.invoke('record-cash-payment', {
        body: {
          student_id: viewingFinance.id,
          branch_id: viewingFinance.branch_id,
          amount: paymentForm.amount,
          date: paymentForm.date,
          notes: paymentForm.notes,
        },
      });

      if (error) throw error;

      window.alert('Pembayaran tunai berhasil dicatat!');
      setIsAddPaymentOpen(false);
      setViewingFinance(null);
      onAddStudent();
    } catch (err: any) {
      window.alert('Gagal: ' + err.message);
    } finally {
      setLoadingPayment(false);
    }
  };

  const handleOpenPromote = async (student: Student) => {
    setPromotingStudent(student);
    setIsPromoteOpen(true);
    setIsLoadingPromoteClasses(true);

    setPromoteFormData({
      class_id: '',
      program: student.program_choice || 'REGULER',
      customProgram: '',
      totalCourseFee: student.financial_record?.total_cost || 0,
      registrationFee: 0,
      discount: 0,
      paymentType: 'CASH',
      installments: 2,
      initialPayment: 0,
      paymentDate: new Date().toISOString().split('T')[0],
      joinDate: new Date().toISOString().split('T')[0],
    });

    try {
      const { data: clsData } = await supabase
        .from('classes')
        .select('*, grade_level:grade_levels(*)')
        .eq('branch_id', student.branch_id)
        .order('name');
      if (clsData) setPromoteClasses(clsData);

      const { data: prData } = await supabase
        .from('program_prices')
        .select('*')
        .eq('branch_id', student.branch_id);
      if (prData) setPromoteProgramPrices(prData);
    } catch (e) {
      /* console removed */
    } finally {
      setIsLoadingPromoteClasses(false);
    }
  };

  const promoteClassAndPrice = useMemo(() => {
    if (!promoteFormData.class_id || !promotingStudent) return { grade: '', priceMatch: null };
    const selectedClass = promoteClasses.find((c) => c.id === promoteFormData.class_id);
    if (!selectedClass) return { grade: '', priceMatch: null };

    const grade = selectedClass.grade_level?.level || selectedClass.name;

    const priceMatch = promoteProgramPrices.find((p) => {
      if (p.program_name.toUpperCase() !== promoteFormData.program.toUpperCase()) return false;
      if (p.grade === 'ALL') return true;
      if (p.grade === grade) return true;

      if (grade.includes('SD') && p.grade === 'SD') return true;
      if (grade.includes('SMP') && p.grade === 'SMP') return true;
      if (grade.includes('SMA') && p.grade === 'SMA') return true;

      return false;
    });

    return { grade, priceMatch };
  }, [
    promoteFormData.class_id,
    promoteFormData.program,
    promoteClasses,
    promoteProgramPrices,
    promotingStudent,
  ]);

  useEffect(() => {
    if (promoteClassAndPrice.priceMatch) {
      const match = promoteClassAndPrice.priceMatch;
      const fee =
        promoteFormData.paymentType === 'ANGSUR'
          ? match.installment_price || match.price
          : match.cash_price || match.price;
      setPromoteFormData((prev) => ({
        ...prev,
        totalCourseFee: fee,
        registrationFee: match.registration_fee || 0,
      }));
    }
  }, [promoteClassAndPrice.priceMatch, promoteFormData.paymentType]);

  const promoteFeeCalculation = useMemo(() => {
    const discountAmount =
      (Number(promoteFormData.totalCourseFee) * Number(promoteFormData.discount)) / 100;
    const finalCourseFee = Number(promoteFormData.totalCourseFee) - discountAmount;
    const totalBill = finalCourseFee + Number(promoteFormData.registrationFee);
    const remainingDebt = Math.max(0, totalBill - Number(promoteFormData.initialPayment));

    return { totalBill, remainingDebt, discountAmount };
  }, [
    promoteFormData.totalCourseFee,
    promoteFormData.discount,
    promoteFormData.registrationFee,
    promoteFormData.initialPayment,
  ]);

  const handlePromoteSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!promotingStudent || !promoteFormData.class_id) {
      return window.alert('Mohon lengkapi dan pilih kelas baru siswa.');
    }

    setIsSubmittingPromote(true);
    try {
      const selectedClass = promoteClasses.find((c) => c.id === promoteFormData.class_id);
      const newGrade = selectedClass?.grade_level?.level || promotingStudent.grade;
      const finalProgram =
        promoteFormData.program === 'INPUT MANUAL'
          ? promoteFormData.customProgram
          : promoteFormData.program;

      const dpAmount = Number(promoteFormData.initialPayment);
      const initialPayments = [];
      if (dpAmount > 0) {
        initialPayments.push({
          id: 'TRX-PROMOTION-' + Date.now(),
          amount: dpAmount,
          date: promoteFormData.paymentDate,
          method: 'CASH',
          notes: `Bayar Akhir/DP Naik Kelas (${finalProgram} - ${newGrade})`,
        });
      }

      const plan = [];
      const actualCourseFee =
        Number(promoteFormData.totalCourseFee) - Number(promoteFormData.registrationFee);

      if (promoteFormData.paymentType === 'ANGSUR') {
        const perInstallment = Math.ceil(actualCourseFee / Number(promoteFormData.installments));
        for (let i = 1; i <= Number(promoteFormData.installments); i++) {
          plan.push({
            label: `Angsuran ke-${i}`,
            amount: perInstallment,
            status: 'UNPAID',
            due_date: null,
          });
        }

        if (dpAmount > Number(promoteFormData.registrationFee)) {
          let remainingDP = dpAmount - Number(promoteFormData.registrationFee);
          for (let i = 0; i < plan.length; i++) {
            if (remainingDP >= plan[i].amount) {
              plan[i].status = 'PAID';
              remainingDP -= plan[i].amount;
            } else if (remainingDP > 0) {
              break;
            }
          }
        }
      } else {
        plan.push({
          label: 'Pelunasan Program',
          amount: actualCourseFee,
          status: dpAmount >= promoteFeeCalculation.totalBill ? 'PAID' : 'UNPAID',
        });
      }

      const oldRecord = promotingStudent.financial_record || {
        total_cost: 0,
        registration_fee: 0,
        payment_type: 'CASH',
        installment_count: 1,
        payments: [],
        installment_plan: [],
      };

      const oldPeriods = (oldRecord as any).previous_periods || [];
      const currentPeriodArchive = {
        academic_year: `Periode Kelas ${promotingStudent.grade} - ${new Date(promotingStudent.joined_at || Date.now()).getFullYear()}`,
        grade: promotingStudent.grade,
        program_choice: promotingStudent.program_choice || 'REGULER',
        class_room_name: promotingStudent.class_room?.name || 'Sebelumnya',
        total_cost: oldRecord.total_cost,
        registration_fee: oldRecord.registration_fee,
        payment_type: oldRecord.payment_type,
        payments: oldRecord.payments,
        installment_plan: oldRecord.installment_plan,
        archived_at: new Date().toISOString(),
      };

      const newPreviousPeriods = [...oldPeriods, currentPeriodArchive];

      const newFinancialRecord = {
        total_cost: promoteFeeCalculation.totalBill,
        registration_fee: Number(promoteFormData.registrationFee),
        payment_type: promoteFormData.paymentType,
        installment_count:
          promoteFormData.paymentType === 'ANGSUR' ? Number(promoteFormData.installments) : 1,
        payments: initialPayments,
        installment_plan: plan,
        previous_periods: newPreviousPeriods,
      };

      const newNotes =
        `${promotingStudent.notes || ''}\n[SYSTEM: Naik Kelas pada ${new Date().toLocaleDateString('id-ID')} ke ${selectedClass?.name || ''}]`.trim();

      const { error: updateErr } = await supabase
        .from('students')
        .update({
          class_id: promoteFormData.class_id,
          grade: newGrade,
          program_choice: finalProgram,
          joined_at: new Date(promoteFormData.joinDate).toISOString(),
          financial_record: newFinancialRecord,
          notes: newNotes,
        })
        .eq('id', promotingStudent.id);

      if (updateErr) throw updateErr;

      const regFee = Number(promoteFormData.registrationFee);
      const accPeriod = getAccountingPeriod(promoteFormData.paymentDate);
      if (regFee > 0 && dpAmount > 0) {
        const actualRegIncome = Math.min(dpAmount, regFee);
        await supabase.from('branch_incomes').insert({
          branch_id: 'HEADQUARTERS',
          date: promoteFormData.paymentDate,
          amount: actualRegIncome,
          accounting_period: accPeriod,
          description: `REGIS NAIK KELAS: ${promotingStudent.name} (Unit ${promotingStudent.branch_id})`,
        });
      }

      const branchIncome = dpAmount - regFee;
      if (branchIncome > 0) {
        await supabase.from('branch_incomes').insert({
          branch_id: promotingStudent.branch_id,
          date: promoteFormData.paymentDate,
          amount: branchIncome,
          accounting_period: accPeriod,
          description: `DP NAIK KELAS: ${promotingStudent.name}`,
        });
      }

      window.alert(
        `Sukses naik kelas!\nSiswa: ${promotingStudent.name} telah diupdate ke kelas: ${selectedClass?.name || ''} (${newGrade}) dengan skema baru.`
      );
      setIsPromoteOpen(false);
      onAddStudent();
    } catch (err: any) {
      console.error('[PROMOTIONS_ERROR]', err);
      window.alert('Gagal melakukan naik kelas: ' + err.message);
    } finally {
      setIsSubmittingPromote(false);
    }
  };

  const handleDeleteStudent = async (id: string) => {
    if (!window.confirm('Hapus data siswa ini? Tindakan ini tidak dapat dibatalkan.')) return;
    try {
      const { error } = await supabase.from('students').delete().eq('user_id', id);
      if (error) throw error;
      onDeleteStudent(id);
      window.alert('Data siswa berhasil dihapus.');
    } catch (e: any) {
      window.alert('Gagal menghapus siswa: ' + e.message);
    }
  };

  return (
    <div className="space-y-4 animate-fade-in font-sans pb-10">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto items-stretch sm:items-center">
          <div className="relative max-w-xs">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-300 w-3 h-3" />
            <input
              placeholder="Cari nama siswa..."
              className="w-full pl-9 pr-4 py-2 border border-slate-100 rounded-xl text-[11px] font-medium bg-white outline-none focus:border-indigo-300 transition-all"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <div className="bg-slate-100 p-1 rounded-xl flex gap-1 w-fit border border-slate-200/50">
            <button
              type="button"
              onClick={() => setStatusFilter('ACTIVE')}
              className={`px-4 py-1.5 rounded-lg text-[9px] font-black uppercase tracking-wider transition-all ${
                statusFilter === 'ACTIVE'
                  ? 'bg-white text-indigo-700 shadow-sm'
                  : 'text-slate-400 hover:text-slate-600'
              }`}
            >
              Aktif
            </button>
            <button
              type="button"
              onClick={() => setStatusFilter('INACTIVE')}
              className={`px-4 py-1.5 rounded-lg text-[9px] font-black uppercase tracking-wider transition-all ${
                statusFilter === 'INACTIVE'
                  ? 'bg-white text-slate-700 shadow-sm'
                  : 'text-slate-400 hover:text-slate-600'
              }`}
            >
              Selesai / Nonaktif
            </button>
            <button
              type="button"
              onClick={() => setStatusFilter('ALL')}
              className={`px-4 py-1.5 rounded-lg text-[9px] font-black uppercase tracking-wider transition-all ${
                statusFilter === 'ALL'
                  ? 'bg-white text-slate-700 shadow-sm'
                  : 'text-slate-400 hover:text-slate-600'
              }`}
            >
              Semua
            </button>
          </div>
        </div>
        {canAddStudent && (
          <button
            onClick={() => setIsFormOpen(true)}
            className="bg-indigo-600 text-white px-5 py-2.5 rounded-xl font-black uppercase text-[9px] shadow-md flex gap-2 items-center hover:bg-indigo-700 transition-all"
          >
            <UserPlus className="w-4 h-4" /> Tambah Siswa
          </button>
        )}
      </div>

      <div className="bg-white rounded-[2rem] shadow-sm border border-slate-100 overflow-x-auto no-scrollbar">
        <table className="w-full text-[10px]">
          <thead className="bg-slate-50 border-b text-slate-400 font-black uppercase tracking-widest">
            <tr>
              <th className="p-5 text-left">Siswa Identity</th>
              <th className="p-5 text-left">Kelas (Dinamis)</th>
              {canViewFinance && <th className="p-5 text-center">Status Keuangan</th>}
              <th className="p-5 text-center w-36">Opsi</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-50">
            {filtered.map((s) => {
              const totalCost = s.financial_record?.total_cost || 0;
              const paid =
                s.financial_record?.payments?.reduce(
                  (a: number, b: any) => a + (Number(b.amount) || 0),
                  0
                ) || 0;
              const remaining = totalCost - paid;
              const isLunas = remaining <= 0;
              const isActive = s.is_active !== false;
              return (
                <tr
                  key={s.id}
                  className={`hover:bg-slate-50 transition-colors group text-slate-900 ${!isActive ? 'bg-slate-50/40 opacity-75' : ''}`}
                >
                  <td className="p-5">
                    <div className="flex items-center gap-2">
                      <div className="font-black uppercase tracking-tight text-slate-800">
                        {s.name}
                      </div>
                      {!isActive && (
                        <span className="text-[7px] font-black uppercase bg-amber-100 text-amber-800 px-2 py-0.5 rounded-md tracking-wider border border-amber-200">
                          NONAKTIF
                        </span>
                      )}
                    </div>
                    <div className="text-[8px] text-slate-400 font-bold mt-0.5 uppercase tracking-tighter">
                      {s.email}
                    </div>
                  </td>
                  <td className="p-5">
                    <div className="font-bold text-slate-600 uppercase flex items-center gap-2">
                      <Layers className="w-3 h-3 text-indigo-400" />
                      {s.class_room?.name ?? '—'}
                    </div>
                    <div className="text-[8px] text-indigo-500 font-black uppercase mt-0.5 tracking-widest">
                      {s.program_choice || 'REGULER'}
                    </div>
                  </td>
                  {canViewFinance && (
                    <td className="p-5 text-center">
                      <button
                        onClick={() => setViewingFinance(s)}
                        className={`text-[9px] font-black uppercase tracking-tight px-4 py-1.5 rounded-full border transition-all ${isLunas ? 'bg-emerald-50 text-emerald-600 border-emerald-100 shadow-sm shadow-emerald-50' : 'bg-rose-50 text-rose-600 border-rose-100'}`}
                      >
                        {isLunas ? 'LUNAS ✅' : `SISA RP ${remaining.toLocaleString()}`}
                      </button>
                    </td>
                  )}
                  <td className="p-5 text-center">
                    <div className="flex justify-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      {canViewFinance && (
                        <button
                          onClick={() => setViewingFinance(s)}
                          title="Kartu Keuangan"
                          className="text-slate-300 hover:text-indigo-600 p-2 bg-slate-50 rounded-xl"
                        >
                          <DollarSign className="w-4 h-4" />
                        </button>
                      )}
                      {isActive && canAddStudent && (
                        <button
                          onClick={() => handleOpenPromote(s)}
                          title="Naik Kelas / Perpanjang Program"
                          className="text-slate-300 hover:text-emerald-600 p-2 bg-slate-50 rounded-xl"
                        >
                          <RefreshCw className="w-4 h-4" />
                        </button>
                      )}
                      {canAddStudent && (
                        <button
                          onClick={() => handleToggleStatus(s)}
                          disabled={isStatusChanging === s.id}
                          title={
                            isActive
                              ? 'Nonaktifkan Siswa (Selesai/Berhenti)'
                              : 'Aktifkan Kembali Siswa'
                          }
                          className={`p-2 rounded-xl transition-all ${
                            isActive
                              ? 'text-slate-300 hover:text-amber-600 bg-slate-50 hover:bg-amber-50'
                              : 'text-amber-500 hover:text-emerald-600 bg-amber-50 hover:bg-emerald-50'
                          }`}
                        >
                          {isStatusChanging === s.id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : isActive ? (
                            <UserX className="w-4 h-4" />
                          ) : (
                            <UserCheck className="w-4 h-4" />
                          )}
                        </button>
                      )}
                      {canAddStudent && (
                        <button
                          onClick={() => handleDeleteStudent(s.user_id || s.id)}
                          className="text-slate-300 hover:text-rose-500 p-2 bg-slate-50 rounded-xl"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                      {!canAddStudent && !canViewFinance && (
                        <span className="text-slate-200 uppercase font-black text-[7px] tracking-widest">
                          Read Only
                        </span>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
            {filtered.length === 0 && (
              <tr>
                <td
                  colSpan={canViewFinance ? 4 : 3}
                  className="p-10 text-center text-slate-300 font-bold uppercase tracking-widest text-[9px]"
                >
                  Tidak ada data siswa ditemukan
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {isFormOpen && canAddStudent && (
        <div className="fixed inset-0 bg-slate-900/60 z-[100] flex items-center justify-center p-4 backdrop-blur-md overflow-hidden">
          <div className="bg-white p-10 md:p-12 rounded-[3.5rem] w-full max-w-6xl max-h-[95vh] overflow-y-auto shadow-2xl animate-fade-in-up border border-white no-scrollbar">
            <div className="absolute top-10 right-10 flex gap-2">
              <button
                onClick={() => {
                  if (window.confirm('Hapus draft input?')) {
                    localStorage.removeItem(FORM_STORAGE_KEY);
                    window.location.reload();
                  }
                }}
                className="p-3 bg-slate-50 text-slate-400 rounded-full hover:bg-rose-50 hover:text-rose-500 transition-all"
                title="Reset Draft"
              >
                <RefreshCw className="w-5 h-5" />
              </button>
              <button
                onClick={() => setIsFormOpen(false)}
                className="p-3 bg-slate-50 text-slate-400 rounded-full hover:bg-slate-200 transition-all"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="text-center mb-12">
              <h3 className="font-black text-3xl md:text-4xl text-slate-900 uppercase tracking-tighter leading-none">
                Registrasi Siswa
              </h3>
              <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest mt-3">
                Sistem Kelas Dinamis Berbasis Data
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-10">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
                <div className="space-y-5">
                  <p className="text-[10px] font-black text-indigo-600 uppercase border-b-2 border-indigo-50 pb-2 flex items-center gap-2 tracking-widest">
                    <Shield className="w-3.5 h-3.5" /> 1. Data Akun
                  </p>
                  <InputMinimal
                    label="Nama Lengkap Siswa"
                    value={formData.name}
                    onChange={(v) => setFormData({ ...formData, name: v })}
                    icon={<User className="w-3.5 h-3.5" />}
                  />
                  <InputMinimal
                    label="Email / Username"
                    value={formData.email}
                    onChange={(v) => setFormData({ ...formData, email: v })}
                    placeholder="siswa@delta.com"
                    icon={<Shield className="w-3.5 h-3.5" />}
                  />
                  <div className="space-y-1">
                    <label className="text-[9px] font-black text-slate-400 uppercase ml-2 tracking-widest">
                      <Key className="w-3 h-3" /> Password Akun
                    </label>
                    <input
                      type="text"
                      className="w-full border-2 border-slate-50 p-4 rounded-2xl font-bold bg-slate-50/50 text-[11px] outline-none focus:border-indigo-100 transition-all"
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      required
                    />
                  </div>
                  <div className="space-y-1">
                    <label className="text-[9px] font-black text-indigo-600 uppercase ml-2 tracking-widest">
                      <Calendar className="w-3 h-3" /> Tanggal Aktif (Join)
                    </label>
                    <input
                      type="date"
                      className="w-full border-2 border-indigo-50 p-4 rounded-2xl font-black bg-indigo-50/10 text-indigo-900 text-xs outline-none focus:border-indigo-300"
                      value={formData.joinDate}
                      onChange={(e) => setFormData({ ...formData, joinDate: e.target.value })}
                      required
                    />
                  </div>
                </div>
                <div className="space-y-5">
                  <p className="text-[10px] font-black text-emerald-600 uppercase border-b-2 border-emerald-50 pb-2 flex items-center gap-2 tracking-widest">
                    <User className="w-3.5 h-3.5" /> 2. Biodata & Kontak
                  </p>
                  <InputMinimal
                    label="Nama Orang Tua / Wali"
                    value={formData.parent_name}
                    onChange={(v) => setFormData({ ...formData, parent_name: v })}
                    icon={<User className="w-3.5 h-3.5" />}
                  />
                  <div className="grid grid-cols-2 gap-3">
                    <InputMinimal
                      label="WA Siswa"
                      value={formData.phone}
                      onChange={(v) => setFormData({ ...formData, phone: v })}
                      icon={<Phone className="w-3.5 h-3.5" />}
                    />
                    <InputMinimal
                      label="WA Orang Tua"
                      value={formData.parent_phone}
                      onChange={(v) => setFormData({ ...formData, parent_phone: v })}
                      icon={<Phone className="w-3.5 h-3.5" />}
                    />
                  </div>
                  <div className="space-y-1">
                    <label className="text-[9px] font-black text-slate-400 uppercase ml-2 tracking-widest">
                      Asal Sekolah
                    </label>
                    <div className="relative">
                      <School className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-300 w-4 h-4" />
                      <input
                        className="w-full border-2 border-slate-50 pl-11 pr-4 py-4 rounded-2xl font-bold bg-slate-50/50 text-[11px] outline-none"
                        value={formData.school_origin}
                        onChange={(e) =>
                          setFormData({ ...formData, school_origin: e.target.value })
                        }
                      />
                    </div>
                  </div>
                  <div className="space-y-1">
                    <label className="text-[9px] font-black text-indigo-600 uppercase ml-2 tracking-widest flex items-center gap-2">
                      <Layers className="w-3 h-3" /> Pilih Kelas (Dinamis)
                    </label>
                    <select
                      className="w-full border-2 border-indigo-50 p-4 rounded-2xl font-black bg-white text-[11px] outline-none appearance-none cursor-pointer focus:ring-4 focus:ring-indigo-500/10 transition-all"
                      value={formData.class_id}
                      onChange={(e) => setFormData({ ...formData, class_id: e.target.value })}
                      required
                    >
                      <option value="">-- PILIH KELAS AKTIF --</option>
                      {isLoadingClasses ? (
                        <option disabled>Loading classes...</option>
                      ) : (
                        availableClasses.map((cls) => (
                          <option key={cls.id} value={cls.id}>
                            {cls.name.toUpperCase()}
                          </option>
                        ))
                      )}
                    </select>
                  </div>
                </div>
                <div className="space-y-5">
                  <p className="text-[10px] font-black text-rose-600 uppercase border-b-2 border-rose-50 pb-2 flex items-center gap-2 tracking-widest">
                    <DollarSign className="w-3.5 h-3.5" /> 3. Skema Keuangan
                  </p>
                  <div className="bg-slate-50 p-6 rounded-[2.5rem] border border-slate-100 space-y-4 shadow-inner">
                    <div className="space-y-1">
                      <label className="text-[9px] font-black text-slate-400 uppercase ml-1">
                        Pilih Program
                      </label>
                      <select
                        className="w-full border-2 border-white p-3.5 rounded-xl font-black bg-white text-[11px] outline-none text-indigo-700"
                        value={formData.program}
                        onChange={(e) => setFormData({ ...formData, program: e.target.value })}
                      >
                        <option value="REGULER">REGULER</option>
                        <option value="UTBK">UTBK</option>
                        <option value="TKAA">TKAA</option>
                        <option value="INPUT MANUAL">MANUAL / CUSTOM</option>
                      </select>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-1">
                        <label className="text-[9px] font-black text-indigo-600 uppercase ml-1">
                          Harga Pokok
                        </label>
                        <input
                          type="number"
                          className="w-full border-2 border-white p-3.5 rounded-xl font-black bg-white text-[11px] outline-none text-indigo-900"
                          value={formData.totalCourseFee}
                          onChange={(e) =>
                            setFormData({ ...formData, totalCourseFee: Number(e.target.value) })
                          }
                        />
                      </div>
                    </div>
                    <div className="space-y-1">
                      <label className="text-[9px] font-black text-slate-400 uppercase ml-1">
                        Biaya Pendaftaran
                      </label>
                      <input
                        type="number"
                        className="w-full border-2 border-white p-3.5 rounded-xl font-bold bg-white text-[11px] outline-none"
                        value={formData.registrationFee}
                        onChange={(e) =>
                          setFormData({ ...formData, registrationFee: Number(e.target.value) })
                        }
                      />
                    </div>
                    <div className="pt-4 border-t-2 border-white flex justify-between items-center text-[10px] font-black uppercase text-indigo-600">
                      <span>Total Tagihan:</span>
                      <span className="text-sm">
                        Rp {feeCalculation.totalBill.toLocaleString()}
                      </span>
                    </div>
                  </div>
                  <div className="bg-indigo-600 p-8 rounded-[3rem] text-white shadow-2xl shadow-indigo-100">
                    <div className="space-y-1">
                      <label className="text-[9px] font-black uppercase ml-1 flex items-center gap-2 tracking-widest">
                        <ChevronDown className="w-4 h-4" /> Metode Pembayaran
                      </label>
                      <div className="relative">
                        <select
                          className="w-full bg-white/20 border-none p-4 rounded-2xl font-black text-white outline-none appearance-none cursor-pointer"
                          value={formData.paymentType}
                          onChange={(e) =>
                            setFormData({ ...formData, paymentType: e.target.value as any })
                          }
                        >
                          <option value="CASH" className="text-slate-900 font-bold">
                            LUNAS (CASH)
                          </option>
                          <option value="ANGSUR" className="text-slate-900 font-bold">
                            ANGSURAN (CICILAN)
                          </option>
                        </select>
                      </div>
                    </div>
                    <div className="space-y-1">
                      <label className="text-[9px] font-black uppercase ml-1 tracking-widest">
                        Tanggal Pembayaran
                      </label>
                      <input
                        type="date"
                        className="w-full bg-white text-indigo-700 p-4 rounded-2xl font-black outline-none text-xs"
                        value={formData.paymentDate}
                        onChange={(e) => setFormData({ ...formData, paymentDate: e.target.value })}
                      />
                    </div>
                  </div>
                </div>
              </div>
              <button
                disabled={isSubmitting}
                className="w-full bg-slate-900 text-white py-8 rounded-[3rem] font-black uppercase tracking-[0.4em] shadow-2xl hover:bg-black hover:scale-[1.01] active:scale-95 transition-all text-xs flex justify-center items-center gap-6"
              >
                {isSubmitting ? (
                  <Loader2 className="animate-spin w-6 h-6" />
                ) : (
                  <CheckCircle className="w-6 h-6" />
                )}{' '}
                PROSES & AKTIFKAN DATABASE SISWA
              </button>
            </form>
          </div>
        </div>
      )}

      {viewingFinance && canViewFinance && (
        <div className="fixed inset-0 bg-slate-900/60 z-[200] flex items-center justify-center p-4 backdrop-blur-md">
          <div className="bg-white p-10 md:p-14 rounded-[3.5rem] w-full max-w-5xl shadow-2xl relative animate-fade-in-up border border-white overflow-hidden max-h-[90vh] flex flex-col no-scrollbar">
            <button
              onClick={() => {
                setViewingFinance(null);
                setIsAddPaymentOpen(false);
              }}
              className="absolute top-10 right-10 text-slate-300 hover:text-slate-900"
            >
              <X className="w-6 h-6" />
            </button>
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-5 mb-10 shrink-0">
              <div className="flex items-center gap-5">
                <div className="w-16 h-16 bg-indigo-600 text-white rounded-2xl flex items-center justify-center shadow-xl shadow-indigo-100">
                  <History className="w-10 h-10" />
                </div>
                <div>
                  <h3 className="font-black text-3xl text-slate-900 uppercase tracking-tighter leading-none">
                    Kartu Kendali Keuangan
                  </h3>
                  <p className="text-[11px] text-slate-400 font-bold uppercase tracking-widest mt-2">
                    {viewingFinance.name} • {viewingFinance.branch_id}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setIsAddPaymentOpen(!isAddPaymentOpen)}
                className="bg-emerald-600 text-white px-8 py-3 rounded-2xl font-black uppercase text-[10px] tracking-widest shadow-xl flex items-center gap-2 hover:bg-emerald-700 transition-all"
              >
                {isAddPaymentOpen ? <X className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
                {isAddPaymentOpen ? 'Tutup Panel Bayar' : 'Catat Bayar Tunai'}
              </button>
            </div>

            <div className="flex-1 overflow-y-auto no-scrollbar space-y-10 pr-2">
              {isAddPaymentOpen && (
                <div className="bg-emerald-50 p-10 rounded-[3rem] border border-emerald-100 animate-fade-in space-y-8 shadow-inner">
                  <div className="flex items-center gap-3">
                    <Banknote className="text-emerald-600 w-6 h-6" />
                    <h4 className="font-black text-emerald-900 uppercase text-xs tracking-widest">
                      Input Transaksi Kasir (Tunai/Lunas Langsung)
                    </h4>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="space-y-1">
                      <label className="text-[9px] font-black text-emerald-600 uppercase ml-2 tracking-widest">
                        Nominal Bayar (Rp)
                      </label>
                      <input
                        type="number"
                        className="w-full border-2 border-white p-4 rounded-2xl font-black text-xl text-emerald-900 bg-white outline-none focus:ring-4 focus:ring-emerald-500/10 transition-all"
                        value={paymentForm.amount}
                        onChange={(e) =>
                          setPaymentForm({ ...paymentForm, amount: Number(e.target.value) })
                        }
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="text-[9px] font-black text-emerald-600 uppercase ml-2 tracking-widest">
                        Catatan
                      </label>
                      <input
                        className="w-full border-2 border-white p-4 rounded-2xl font-bold bg-white outline-none"
                        value={paymentForm.notes}
                        onChange={(e) => setPaymentForm({ ...paymentForm, notes: e.target.value })}
                        placeholder="Misal: Pelunasan SPP"
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="text-[9px] font-black text-emerald-600 uppercase ml-2 tracking-widest">
                        Tanggal
                      </label>
                      <input
                        type="date"
                        className="w-full border-2 border-white p-4 rounded-2xl font-bold bg-white outline-none"
                        value={paymentForm.date}
                        onChange={(e) => setPaymentForm({ ...paymentForm, date: e.target.value })}
                      />
                    </div>
                  </div>
                  <button
                    onClick={handleManualPayment}
                    disabled={loadingPayment}
                    className="w-full bg-slate-900 text-white py-5 rounded-[2rem] font-black uppercase tracking-widest shadow-xl justify-center items-center gap-3 hover:bg-black transition-all active:scale-95 disabled:opacity-50 flex"
                  >
                    {loadingPayment ? (
                      <Loader2 className="animate-spin w-5 h-5" />
                    ) : (
                      <Save className="w-5 h-5" />
                    )}
                    KONFIRMASI PEMBAYARAN TUNAI
                  </button>
                </div>
              )}

              <div className="space-y-6">
                <div className="flex items-center gap-3">
                  <History className="text-slate-400 w-5 h-5" />
                  <h4 className="font-black text-slate-800 uppercase text-xs tracking-widest">
                    Riwayat Transaksi Masuk
                  </h4>
                </div>
                <div className="bg-slate-50 rounded-[2.5rem] border border-slate-100 overflow-hidden shadow-inner">
                  <table className="w-full text-[10px]">
                    <thead className="bg-slate-100/50 border-b text-slate-400 font-black uppercase tracking-widest">
                      <tr>
                        <th className="p-5 text-left">Tanggal</th>
                        <th className="p-5 text-left">Keterangan</th>
                        <th className="p-5 text-right">Nominal</th>
                        <th className="p-5 text-center w-32">Aksi</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {(viewingFinance.financial_record?.payments || []).map(
                        (p: any, idx: number) => {
                          const isVoidEntry = Number(p.amount) < 0 || p.is_void;
                          return (
                            <tr
                              key={idx}
                              className={`font-bold transition-colors ${isVoidEntry ? 'bg-rose-50/40 text-rose-400' : 'hover:bg-white text-slate-600'}`}
                            >
                              <td className="p-5">
                                {new Date(p.date).toLocaleDateString('id-ID')}
                              </td>
                              <td className="p-5 uppercase">
                                <div className="flex items-center gap-2">
                                  {p.notes || 'Pembayaran'}
                                  {isVoidEntry && (
                                    <span className="text-[7px] bg-rose-200 text-rose-700 px-1.5 py-0.5 rounded-full font-black">
                                      VOIDED
                                    </span>
                                  )}
                                </div>
                              </td>
                              <td className="p-5 text-right font-black">
                                <span
                                  className={`${isVoidEntry ? 'text-rose-500 line-through' : 'text-indigo-600'}`}
                                >
                                  Rp {Number(p.amount).toLocaleString()}
                                </span>
                              </td>
                              <td className="p-5 text-center">
                                <div className="flex justify-center gap-2">
                                  {!isVoidEntry && (
                                    <>
                                      <button
                                        onClick={() => handlePrintReceipt(viewingFinance, p)}
                                        className="p-2 bg-white border border-slate-200 rounded-xl text-slate-400 hover:text-indigo-600 hover:border-indigo-200 transition-all shadow-sm"
                                        title="Cetak Kwitansi"
                                      >
                                        <Printer className="w-4 h-4" />
                                      </button>
                                      <button
                                        onClick={() =>
                                          handleVoidPayment(viewingFinance.id, p.id, p.amount)
                                        }
                                        disabled={!!isVoiding}
                                        className="p-2 bg-white border border-slate-200 rounded-xl text-slate-400 hover:text-rose-600 hover:border-rose-200 transition-all shadow-sm disabled:opacity-30"
                                        title="Void Transaksi (Batalkan)"
                                      >
                                        {isVoiding === p.id ? (
                                          <Loader2 className="w-4 h-4 animate-spin" />
                                        ) : (
                                          <RefreshCw className="w-4 h-4" />
                                        )}
                                      </button>
                                    </>
                                  )}
                                </div>
                              </td>
                            </tr>
                          );
                        }
                      )}
                      {(viewingFinance.financial_record?.payments?.length || 0) === 0 && (
                        <tr>
                          <td
                            colSpan={4}
                            className="p-10 text-center text-slate-300 font-black uppercase tracking-widest"
                          >
                            Belum ada transaksi
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {isPromoteOpen && promotingStudent && (
        <div className="fixed inset-0 bg-slate-900/60 z-[250] flex items-center justify-center p-4 backdrop-blur-md overflow-hidden animate-fade-in">
          <div className="bg-white p-10 md:p-12 rounded-[3.5rem] w-full max-w-6xl max-h-[95vh] overflow-y-auto shadow-2xl relative border border-white no-scrollbar animate-fade-in-up">
            <button
              type="button"
              onClick={() => setIsPromoteOpen(false)}
              className="absolute top-10 right-10 text-slate-300 hover:text-slate-900 p-2"
            >
              <X className="w-6 h-6" />
            </button>
            <div className="text-center mb-10 font-sans">
              <h3 className="font-black text-3xl md:text-4xl text-emerald-600 uppercase tracking-tighter leading-none flex items-center justify-center gap-3">
                <RefreshCw className="w-8 h-8 animate-spin-slow" /> Naik Kelas & Perpanjang Program
              </h3>
              <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest mt-3">
                Promosikan Siswa ke Tingkat Akademik Selanjutnya
              </p>
            </div>

            <form onSubmit={handlePromoteSubmit} className="space-y-10 font-sans">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
                <div className="space-y-5 bg-slate-50/50 p-6 rounded-3xl border border-slate-100">
                  <p className="text-[10px] font-black text-indigo-600 uppercase border-b-2 border-indigo-50 pb-2 flex items-center gap-2 tracking-widest">
                    <User className="w-3.5 h-3.5" /> 1. Biodata Siswa
                  </p>
                  <div className="space-y-3 text-[11px] text-slate-900">
                    <div>
                      <label className="text-[8px] font-black text-slate-400 uppercase tracking-widest">
                        Nama Lengkap
                      </label>
                      <p className="font-extrabold text-slate-800 uppercase">
                        {promotingStudent.name}
                      </p>
                    </div>
                    <div>
                      <label className="text-[8px] font-black text-slate-400 uppercase tracking-widest">
                        Email Terdaftar
                      </label>
                      <p className="font-bold text-slate-600">{promotingStudent.email}</p>
                    </div>
                    <div>
                      <label className="text-[8px] font-black text-slate-400 uppercase tracking-widest">
                        Kelas & Grade Saat Ini
                      </label>
                      <p className="font-black text-indigo-600 uppercase mt-0.5">
                        {promotingStudent.class_room?.name || 'Belum ada kelas'} (
                        {promotingStudent.grade})
                      </p>
                    </div>
                    <div>
                      <label className="text-[8px] font-black text-slate-400 uppercase tracking-widest">
                        Program Lama
                      </label>
                      <p className="font-bold text-slate-600 uppercase">
                        {promotingStudent.program_choice || 'REGULER'}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="space-y-5">
                  <p className="text-[10px] font-black text-emerald-600 uppercase border-b-2 border-emerald-50 pb-2 flex items-center gap-2 tracking-widest">
                    <School className="w-3.5 h-3.5" /> 2. Kelas & Akademi Baru
                  </p>
                  <div className="space-y-1 text-slate-950">
                    <label className="text-[9px] font-black text-emerald-600 uppercase ml-2 tracking-widest flex items-center gap-2">
                      Pilih Kelas Baru
                    </label>
                    <select
                      className="w-full border-2 border-emerald-50 p-4 rounded-2xl font-black bg-white text-[11px] outline-none appearance-none cursor-pointer focus:ring-4 focus:ring-emerald-500/10 transition-all text-slate-950"
                      value={promoteFormData.class_id}
                      onChange={(e) =>
                        setPromoteFormData({ ...promoteFormData, class_id: e.target.value })
                      }
                      required
                    >
                      <option value="">-- PILIH KELAS BARU --</option>
                      {isLoadingPromoteClasses ? (
                        <option disabled>Loading classes...</option>
                      ) : (
                        promoteClasses.map((cls) => (
                          <option key={cls.id} value={cls.id}>
                            {cls.name.toUpperCase()} ({cls.grade_level?.level || 'Grade'})
                          </option>
                        ))
                      )}
                    </select>
                  </div>
                  <div className="space-y-1 text-slate-950">
                    <label className="text-[9px] font-black text-slate-400 uppercase ml-2 tracking-widest">
                      Pilih Program Naik Kelas
                    </label>
                    <select
                      className="w-full border-2 border-slate-100 p-4 rounded-2xl font-black bg-white text-[11px] outline-none text-indigo-700 font-bold cursor-pointer"
                      value={promoteFormData.program}
                      onChange={(e) =>
                        setPromoteFormData({ ...promoteFormData, program: e.target.value })
                      }
                    >
                      <option value="REGULER">REGULER</option>
                      <option value="UTBK">UTBK</option>
                      <option value="TKAA">TKAA</option>
                      <option value="INPUT MANUAL">MANUAL / CUSTOM</option>
                    </select>
                  </div>
                  {promoteFormData.program === 'INPUT MANUAL' && (
                    <InputMinimal
                      label="Nama Program Custom"
                      value={promoteFormData.customProgram}
                      onChange={(v) => setPromoteFormData({ ...promoteFormData, customProgram: v })}
                    />
                  )}
                  <div className="space-y-1 text-slate-950">
                    <label className="text-[9px] font-black text-slate-400 uppercase ml-2 tracking-widest">
                      Tanggal Mulai Baru
                    </label>
                    <input
                      type="date"
                      className="w-full border-2 border-slate-100 p-4 rounded-2xl font-bold bg-white text-[11px] outline-none text-slate-950"
                      value={promoteFormData.joinDate}
                      onChange={(e) =>
                        setPromoteFormData({ ...promoteFormData, joinDate: e.target.value })
                      }
                      required
                    />
                  </div>
                </div>

                <div className="space-y-5">
                  <p className="text-[10px] font-black text-rose-600 uppercase border-b-2 border-rose-50 pb-2 flex items-center gap-2 tracking-widest">
                    <DollarSign className="w-3.5 h-3.5" /> 3. Rincian Biaya
                  </p>
                  <div className="bg-slate-50 p-6 rounded-[2.5rem] border border-slate-100 space-y-4 shadow-inner text-slate-950 border-slate-100">
                    <div className="grid grid-cols-2 gap-3 text-slate-950">
                      <div className="space-y-1">
                        <label className="text-[9px] font-black text-indigo-600 uppercase ml-1">
                          Harga Program
                        </label>
                        <input
                          type="number"
                          className="w-full border-2 border-white p-3 rounded-xl font-black bg-white text-[11px] outline-none text-indigo-900 animate-fade-in"
                          value={promoteFormData.totalCourseFee}
                          onChange={(e) =>
                            setPromoteFormData({
                              ...promoteFormData,
                              totalCourseFee: Number(e.target.value),
                            })
                          }
                        />
                      </div>
                      <div className="space-y-1">
                        <label className="text-[9px] font-black text-rose-600 uppercase ml-1">
                          Registrasi
                        </label>
                        <input
                          type="number"
                          className="w-full border-2 border-white p-3 rounded-xl font-black bg-white text-[11px] outline-none text-rose-900 animate-fade-in"
                          value={promoteFormData.registrationFee}
                          onChange={(e) =>
                            setPromoteFormData({
                              ...promoteFormData,
                              registrationFee: Number(e.target.value),
                            })
                          }
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3 animate-fade-in text-slate-905">
                      <div className="space-y-1 text-slate-950">
                        <label className="text-[9px] font-black text-slate-400 uppercase ml-1">
                          Diskon (%)
                        </label>
                        <input
                          type="number"
                          className="w-full border-2 border-white p-3 rounded-xl font-bold bg-white text-[11px] outline-none text-slate-950 animate-fade-in"
                          value={promoteFormData.discount}
                          onChange={(e) =>
                            setPromoteFormData({
                              ...promoteFormData,
                              discount: Number(e.target.value),
                            })
                          }
                        />
                      </div>
                      <div className="space-y-1 text-slate-950">
                        <label className="text-[9px] font-black text-indigo-600 uppercase ml-1">
                          DP / Bayar Awal
                        </label>
                        <input
                          type="number"
                          className="w-full border-2 border-white p-3 rounded-xl font-black bg-indigo-50 text-[11px] outline-none text-slate-950 animate-fade-in"
                          value={promoteFormData.initialPayment}
                          onChange={(e) =>
                            setPromoteFormData({
                              ...promoteFormData,
                              initialPayment: Number(e.target.value),
                            })
                          }
                        />
                      </div>
                    </div>
                    <div className="pt-4 border-t-2 border-white flex justify-between items-center text-[10px] font-black uppercase text-indigo-600">
                      <span>Total Tagihan:</span>
                      <span className="text-sm">
                        Rp {promoteFeeCalculation.totalBill.toLocaleString()}
                      </span>
                    </div>
                  </div>

                  <div className="bg-indigo-600 p-6 rounded-3xl text-white space-y-3">
                    <div className="space-y-1 text-slate-950">
                      <label className="text-[9px] font-black uppercase ml-1 tracking-widest text-indigo-100">
                        Metode Pembayaran
                      </label>
                      <select
                        className="w-full bg-white/20 border-none p-3 rounded-xl font-black text-white outline-none appearance-none cursor-pointer text-xs"
                        value={promoteFormData.paymentType}
                        onChange={(e) =>
                          setPromoteFormData({
                            ...promoteFormData,
                            paymentType: e.target.value as any,
                          })
                        }
                      >
                        <option value="CASH" className="text-slate-900 font-bold">
                          LUNAS (CASH)
                        </option>
                        <option value="ANGSUR" className="text-slate-900 font-bold">
                          ANGSURAN (CICILAN)
                        </option>
                      </select>
                    </div>
                    {promoteFormData.paymentType === 'ANGSUR' && (
                      <div className="space-y-1 text-slate-950">
                        <label className="text-[9px] font-black uppercase ml-1 tracking-widest text-indigo-100">
                          Jumlah Angsuran
                        </label>
                        <input
                          type="number"
                          className="w-full bg-white text-indigo-700 p-3 rounded-xl font-black outline-none text-xs"
                          value={promoteFormData.installments}
                          onChange={(e) =>
                            setPromoteFormData({
                              ...promoteFormData,
                              installments: Number(e.target.value),
                            })
                          }
                          min={2}
                          max={12}
                        />
                      </div>
                    )}
                    <div className="space-y-1 text-white">
                      <label className="text-[9px] font-black uppercase ml-1 tracking-widest text-indigo-100">
                        Tanggal Pembayaran
                      </label>
                      <input
                        type="date"
                        className="w-full bg-white text-indigo-700 p-3 rounded-xl font-black outline-none text-xs border-none"
                        value={promoteFormData.paymentDate}
                        onChange={(e) =>
                          setPromoteFormData({ ...promoteFormData, paymentDate: e.target.value })
                        }
                      />
                    </div>
                  </div>
                </div>
              </div>

              <button
                disabled={isSubmittingPromote}
                type="submit"
                className="w-full bg-slate-900 text-white py-6 rounded-[2.5rem] font-black uppercase tracking-[0.34em] shadow-2xl hover:bg-black hover:scale-[1.01] active:scale-95 transition-all text-xs flex justify-center items-center gap-4 animate-fade-in"
              >
                {isSubmittingPromote ? (
                  <Loader2 className="animate-spin w-5 h-5" />
                ) : (
                  <CheckCircle className="w-5 h-5" />
                )}{' '}
                KONFIRMASI NAIK KELAS & PROGRAM BARU
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

const InputMinimal = ({
  label,
  value,
  onChange,
  placeholder,
  icon,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  icon?: any;
}) => (
  <div className="space-y-1">
    <label className="text-[9px] font-black text-slate-400 uppercase ml-2 tracking-widest">
      {label}
    </label>
    <div className="relative">
      {icon && (
        <div className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-300">{icon}</div>
      )}
      <input
        className={`w-full border-2 border-slate-50 ${icon ? 'pl-11' : 'pl-4'} pr-4 py-4 rounded-2xl font-bold bg-slate-50/50 text-[11px] outline-none focus:border-indigo-200 transition-all shadow-inner`}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
      />
    </div>
  </div>
);
